import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import {
  faBug,
  faCheckCircle,
  faCubes,
  faFingerprint,
  faKey,
  faRightLeft,
  faRobot,
  faRocket,
  faServer,
  faSitemap,
  faUserShield,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import fgaCheckingAccess from "../../../../../docs/authorization/checking-access.md?raw";
import fgaManagingAccess from "../../../../../docs/authorization/managing-access.md?raw";
import fgaModeling from "../../../../../docs/authorization/modeling.md?raw";
import fgaPatterns from "../../../../../docs/authorization/patterns-and-troubleshooting.md?raw";
import fgaPlatformLifecycle from "../../../../../docs/authorization/platform-lifecycle.md?raw";
import fgaTokenFlows from "../../../../../docs/authorization/token-flows.md?raw";
import fgaWhoTheCheckRunsAs from "../../../../../docs/authorization/who-the-check-runs-as.md?raw";
import concepts from "../../../../../docs/concepts/concepts.md?raw";
import gsEnvironments from "../../../../../docs/getting-started/environments.md?raw";
import gsGettingStarted from "../../../../../docs/getting-started/getting-started.md?raw";
import identity from "../../../../../docs/identity/identity.md?raw";
import identityOneDirectory from "../../../../../docs/identity/one-directory.md?raw";
import refAuthSdk from "../../../../../docs/reference/authentication-sdk.md?raw";
import refFgaApi from "../../../../../docs/reference/authorization-api.md?raw";
import refFgaModel from "../../../../../docs/reference/authorization-model.md?raw";
import refFgaSdk from "../../../../../docs/reference/authorization-sdk.md?raw";
import refMcpServer from "../../../../../docs/reference/mcp-server.md?raw";
import clusterOnboarding from "../../../../../docs/workload-identity/cluster-onboarding.md?raw";
import workloadIdentity from "../../../../../docs/workload-identity/workload-identity.md?raw";

export interface DocEntry {
  slug: string;
  navLabel: string;
  icon: IconDefinition;
  raw: string;
}

export type DocSectionKey =
  | "concepts"
  | "getting-started"
  | "identity"
  | "authorization"
  | "workload-identity"
  | "reference";

export interface DocSection {
  key: DocSectionKey;
  label: string;
  entries: DocEntry[];
}

export const DOC_SECTIONS: DocSection[] = [
  {
    key: "concepts",
    label: "Concepts",
    entries: [
      { slug: "concepts", navLabel: "Concepts", icon: faCubes, raw: concepts },
    ],
  },
  {
    key: "getting-started",
    label: "Getting started",
    entries: [
      {
        slug: "getting-started",
        navLabel: "Getting started",
        icon: faRocket,
        raw: gsGettingStarted,
      },
      {
        slug: "environments",
        navLabel: "Development and production",
        icon: faServer,
        raw: gsEnvironments,
      },
    ],
  },
  {
    key: "identity",
    label: "Identity & authentication",
    entries: [
      {
        slug: "identity",
        navLabel: "Identity & authentication",
        icon: faFingerprint,
        raw: identity,
      },
      {
        slug: "one-directory",
        navLabel: "One directory for everyone",
        icon: faUsers,
        raw: identityOneDirectory,
      },
    ],
  },
  {
    key: "authorization",
    label: "Authorization",
    entries: [
      {
        slug: "modeling",
        navLabel: "Modeling",
        icon: faSitemap,
        raw: fgaModeling,
      },
      {
        slug: "checking-access",
        navLabel: "Checking access",
        icon: faCheckCircle,
        raw: fgaCheckingAccess,
      },
      {
        slug: "who-the-check-runs-as",
        navLabel: "Who the check runs as",
        icon: faUserShield,
        raw: fgaWhoTheCheckRunsAs,
      },
      {
        slug: "token-flows",
        navLabel: "Token flows",
        icon: faRightLeft,
        raw: fgaTokenFlows,
      },
      {
        slug: "managing-access",
        navLabel: "Managing access",
        icon: faKey,
        raw: fgaManagingAccess,
      },
      {
        slug: "platform-lifecycle",
        navLabel: "Platform lifecycle",
        icon: faCubes,
        raw: fgaPlatformLifecycle,
      },
      {
        slug: "patterns-and-troubleshooting",
        navLabel: "Patterns & troubleshooting",
        icon: faBug,
        raw: fgaPatterns,
      },
    ],
  },
  {
    key: "workload-identity",
    label: "Workload identity",
    entries: [
      {
        slug: "workload-identity",
        navLabel: "Workload identity",
        icon: faFingerprint,
        raw: workloadIdentity,
      },
      {
        slug: "cluster-onboarding",
        navLabel: "Cluster onboarding",
        icon: faFingerprint,
        raw: clusterOnboarding,
      },
    ],
  },
  {
    key: "reference",
    label: "Reference",
    entries: [
      {
        slug: "authorization-model",
        navLabel: "Authorization model",
        icon: faSitemap,
        raw: refFgaModel,
      },
      {
        slug: "authorization-sdk",
        navLabel: "Authorization SDK",
        icon: faCheckCircle,
        raw: refFgaSdk,
      },
      {
        slug: "authentication-sdk",
        navLabel: "Authentication SDK",
        icon: faFingerprint,
        raw: refAuthSdk,
      },
      {
        slug: "authorization-api",
        navLabel: "Authorization API",
        icon: faKey,
        raw: refFgaApi,
      },
      {
        slug: "mcp-server",
        navLabel: "MCP server",
        icon: faRobot,
        raw: refMcpServer,
      },
    ],
  },
];

export function getDoc(
  sectionKey: DocSectionKey,
  slug: string,
): DocEntry | undefined {
  const section = DOC_SECTIONS.find((s) => s.key === sectionKey);
  return section?.entries.find((d) => d.slug === slug);
}

export function getSection(key: DocSectionKey): DocSection | undefined {
  return DOC_SECTIONS.find((s) => s.key === key);
}

export const FIRST_DOC_SLUG = DOC_SECTIONS[0].entries[0].slug;
export const FIRST_DOC_SECTION = DOC_SECTIONS[0].key;
